import React, { useEffect, useState } from 'react'
import { Spinner } from '@/components/spinner';
import { cleanWizardData, useChecklistSubmit, WizardData } from './Model';
import { useWizard, WizardProvider } from '@/common/wizard/wizard';
import { Result } from './Result';
import { useNavigate, useParams } from 'react-router-dom';
import { MainPageLayout } from '@/components/layout/MainLayout';
import { useAppContext } from '@/context/app';
import { BackButton } from '@/components/layout/NavBar';
import { MachinePath } from '../../Common';
import { Machine } from '@/services/model/machine';
import { MachineName } from '@/components/machine';
import { ActivateStep } from './Activate';
import { VisualInspectionStep } from './VisualInspection';
import { TireConditionStep } from './TireCondition';
import { SeatSensorStep } from './SeatSenson';
import { LampsStep } from './Lamps';
import { DrivingStep } from './Driving';
import { HydraulicsStep } from './Hydraulics';
import { SOCStep } from './SOC';
import { OperatorCheckStep } from './OperatorCheck';

const maxStep = 9

export const MachineChecklistWizardBody = ({ goDone, machine }: {
  goDone: (acceptedSteps: string[]) => void
  machine: Machine
}) => {
  // const {services, session, translates} = useAppContext()
  const { step, wizardData } = useWizard<WizardData>()
  const { translates } = useAppContext()
  const submitChecklist = useChecklistSubmit(machine.id)

  const stepRenderers: Array<() => JSX.Element> = [
    () => <VisualInspectionStep machine={machine} />,
    () => <TireConditionStep machine={machine} />,
    () => <OperatorCheckStep machine={machine} />,
    () => <ActivateStep machine={machine} />,
    () => <SeatSensorStep machine={machine} />,
    () => <LampsStep machine={machine} />,
    () => <DrivingStep machine={machine} />,
    () => <HydraulicsStep machine={machine} />,
    () => <SOCStep machine={machine} />,
    () => <Result machine={machine} goDone={async (acceptedSteps) => {
      if (await submitChecklist('success')) goDone(acceptedSteps)
    }} />,
  ]

  const renderStep = stepRenderers[step]
  return <>
    {wizardData.submissionError && <div className='critic-error-box' role='alert'>{translates?.for(wizardData.submissionError)}</div>}
    {wizardData.submitting && <Spinner />}
    <div hidden={wizardData.submitting}>{renderStep ? renderStep() : <Spinner />}</div>
  </>
}

export const MachineChecklistWizard = ({ goDone, machine }: {
  goDone: (acceptedSteps: string[]) => void
  machine: Machine
}) => {
  return <div className='machine-checklist-wizard'>

    {machine.machineType ? <h2><MachineName row={{
      internalNumber: machine.internalNumber,
      type: machine.machineType
    }} /></h2> : null}

    <WizardProvider<WizardData> initialData={cleanWizardData()} maxStep={maxStep}>
      <MachineChecklistWizardBody goDone={goDone} machine={machine} />
    </WizardProvider>
  </div>
}

// export const MachineChecklistWizardPopup = ({ onClose }: {
//   onClose: () => void
// }) => {
//   return <MachineChecklistWizard goDone={onClose} />
// }

export const MachineChecklistWizardPage = () => {
  const { services, session, translates } = useAppContext()
  const mid = useParams().mid
  const [machine, setMachine] = useState<Machine>()
  const [loadError, setLoadError] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    let active = true
    setMachine(undefined)
    setLoadError(false)
    fetch(`/api/communal/machines/byid/${mid}`).then((response) => {
      if (!response.ok) throw new Error('Machine unavailable')
      return response.json()
    }).then((data) => {
      if (active) setMachine(data)
    }).catch(() => { if (active) setLoadError(true) })
    return () => { active = false }
  }, [mid])

  useEffect(() => {
    if (machine && !machine.checklistEnabled) navigate(`/communal/machine/${mid}`, { replace: true })
  }, [machine, mid, navigate])

  if (loadError) return <div className='critic-error-box' role='alert'>{translates?.for('errors.general_error')}</div>

  if (!session || !translates || !machine) {
    return <Spinner />
  }
  if (!machine.checklistEnabled) return null

  return <MainPageLayout pageName='communal-dashboard-details' services={services} translates={translates} session={session} path={[...MachinePath({ session, translates, machine }), {
    title: translates.for(`machine.checklist.wizard.title`)
  }]}
    navbarLeft={<BackButton services={services} translates={translates} fallbackUrl={`/communal/machine/${mid}`} />}>
    <MachineChecklistWizard goDone={() => {
      navigate(`/communal/machine/${mid}/session/dashboard`, { replace: true })
    }} machine={machine} />
  </MainPageLayout>
}
